/**
 * ============================================
 *  GWP MANAGER - Gift With Purchase System
 * ============================================
 */
class GWPManager {
  /**
   * Initialize GWP Manager
   * Loads configuration and sets up event listeners
   */
  constructor() {
    this.config = window.gwpConfig || { tiers: [] };
    this.selectedGifts = new Map();
    this.flickityInstance = null; // Store Flickity carousel instance
    if (!this.config.tiers || this.config.tiers.length === 0) return;
  }

  /**
   * Main update function
   * Fetches cart, calculates tiers, and renders GWP UI
   */
  async update() {
    try {
      const container = document.getElementById('GWPContainer');
      if (!container) return;

      const cart = await fetch('/cart.js').then(r => r.json());
      if (!cart.items || cart.items.length === 0) {
        this.destroy();
        return;
      }
      const total = this.getCartTotal(cart);
      
      let currentTier = null;
      let nextTier = null;
      
      for (const tier of this.config.tiers) {
        if (total >= tier.threshold) {
          currentTier = tier;
        } else if (!nextTier) {
          nextTier = tier;
          break;
        }
      }
      
      this.render(currentTier, nextTier, total, cart);
      await this.removeUnqualifiedGifts(cart, total, currentTier);
    } catch (error) {
      console.error('GWP Error:', error);
    }
  }

  /**
   * Calculate cart total excluding GWP gifts
   * @param {Object} cart - Cart object from /cart.js
   * @returns {Number} Total in cents
   */
  getCartTotal(cart) {
    let total = 0;
    cart.items.forEach(item => {
      if (item.properties?._gwp_gift !== 'true') {
        total += item.final_line_price;
      }
    });
    return total;
  }

  /**
   * Render GWP UI
   * Shows current tier status, progress bar, and gift selector
   * @param {Object} currentTier - Currently unlocked tier
   * @param {Object} nextTier - Next tier to unlock
   * @param {Number} total - Cart total in cents
   * @param {Object} cart - Cart object
   */
  render(currentTier, nextTier, total, cart) {
    const container = document.getElementById('GWPContainer');
    if (!container) return;
    
    const existingProgressFill = container.querySelector('.gwp-progress-fill');
    const currentWidth = existingProgressFill ? parseFloat(existingProgressFill.style.width) || 0 : 0;
    
    let html = '';
    let progressHtml = '';
    let topMessage = ''; // Single message for the top

    // Build progress bar with ALL tiers visible
    if (this.config.tiers && this.config.tiers.length > 0) {
      const maxThreshold = this.config.tiers[this.config.tiers.length - 1].threshold;
      let currentProgress;
      if (total >= maxThreshold) {
        currentProgress = 100;
      } else {
        currentProgress = Math.min(100, (total / maxThreshold) * 100);
      }
      
      // Build tier markers for the progress bar
      const tierMarkers = this.config.tiers.map((tier, index) => {
        const position = (tier.threshold / maxThreshold) * 100;
        const isUnlocked = total >= tier.threshold;
        const tierIcon = tier.type === 'free_shipping' ? 'https://cdn.shopify.com/s/files/1/1174/9088/files/truck-white.png?v=1763941500' : 'https://cdn.shopify.com/s/files/1/1174/9088/files/gift-white.png?v=1763941500';
        const isLastTier = index === this.config.tiers.length - 1;
        
        const tierName = tier.name || 'Gift!';
        const alignmentStyle = isLastTier ? 'right: 0; transform: translate(0, -50%);' : `left: ${position}%;`;
        
        const iconHtml = isLastTier 
          ? `<div class="gwp-tier-icon-container--last"><span class="gwp-tier-icon"><img src="${tierIcon}" width="28" height="28" alt="Tier Icon" /></span></div>`
          : `<span class="gwp-tier-icon"><img src="${tierIcon}" width="28" height="28" alt="Tier Icon" /></span>`;
        
        return `
          <div class="gwp-tier-marker ${isUnlocked ? 'unlocked' : ''} ${isLastTier ? 'gwp-tier-marker--last' : ''}" 
               style="${alignmentStyle};" 
               title="${tier.message}">
            <span class="gwp-tier-threshold">${this.formatMoney(tier.threshold)}</span>
            ${iconHtml}
            <span class="gwp-tier-label">${tierName}</span>
          </div>
        `;
      }).join('');
      
      progressHtml = `
        <div class="gwp-progress-bar">
          <div class="gwp-progress-track">
            <div class="gwp-progress-fill" style="width: ${currentWidth}%" data-target-width="${currentProgress}"></div>
            ${tierMarkers}
          </div>
        </div>
      `;
      
      // Build consolidated top message
      let messageParts = [];
      
      // Add current tier message if unlocked
      if (currentTier) {
        messageParts.push(`<span style="color:#864a2c">✓ ${currentTier.message}</span>`);
      }
      
      // Add next tier progress message
      if (nextTier) {
        const remaining = nextTier.threshold - total;
        messageParts.push(nextTier.progressMessage.replace('{amount}', this.formatMoney(remaining)));
      } else if (currentTier) {
        // All tiers unlocked - show this message
        messageParts.push('ALL REWARDS UNLOCKED!');
      }
      
      // Combine messages with a space or separator
      topMessage = messageParts.join(' ');
    }

    // Show consolidated message at the top
    if (topMessage) {
      html += `<div class="gwp-progress-text ${!nextTier ? 'unlocked' : ''}">${topMessage}</div>`;
    }
    
    // Add progress bar
    html += progressHtml;

    // Add gift selector if it's a gift_product tier (NO additional messages here)
    if (currentTier?.type === 'gift_product') {
      const giftInCart = cart.items.find(item => 
        item.properties?._gwp_gift === 'true' && 
        parseInt(item.properties?._gwp_tier_index) === currentTier.tierIndex
      );
      
      html += this.renderGiftSelector(currentTier, '', '', giftInCart);
    }

    container.innerHTML = html;
    container.style.display = html ? 'block' : 'none';

    // Destroy previous Flickity instance if exists
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
    }

    // Animate progress bar if needed
    const progressFill = container.querySelector('.gwp-progress-fill');
    if (progressFill) {
      const targetWidth = parseFloat(progressFill.dataset.targetWidth);
      
      if (!nextTier || currentWidth !== targetWidth) {
        requestAnimationFrame(() => {
          progressFill.style.width = targetWidth + '%';
        });
      }
    }

    // Initialize Flickity for gift selector if present
    if (currentTier?.type === 'gift_product') {
      this.attachListeners(currentTier, cart);
      this.initFlickity();
    }
  }

  /**
   * Initialize Flickity carousel for gift products
   * Follows the theme's Carousel component pattern
   */
  initFlickity() {
    const slider = document.querySelector('.gwp-product-slider');
    if (!slider) {
      console.log('GWP: No slider found');
      return;
    }
    
    // Wait for next frame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (typeof Flickity !== 'undefined') {
        try {
          // Define config directly instead of parsing from attribute
          const config = {
            prevNextButtons: true,
            pageDots: false,
            wrapAround: false,
            contain: true,
            cellAlign: 'left',
            watchCSS: false, // Disable watchCSS to force initialization
            dragThreshold: 8,
            groupCells: false,
            arrowShape: { x0: 20, x1: 60, y1: 40, x2: 60, y2: 35, x3: 25 }
          };
          
          console.log('GWP: Initializing Flickity');
          this.flickityInstance = new Flickity(slider, config);
          console.log('GWP: Flickity initialized successfully');
        } catch (error) {
          console.error('GWP Flickity Error:', error);
        }
      } else {
        console.error('GWP: Flickity is not defined. Make sure Flickity library is loaded.');
      }
    });
  }

  /**
   * Render gift product selector
   * @param {Object} tier - Tier configuration
   * @param {String} combinedMessage - Combined status and progress message
   * @param {String} progressHtml - Progress bar HTML
   * @param {Object} giftInCart - Gift item currently in cart (if any)
   * @returns {String} HTML string for gift selector
   */
  renderGiftSelector(tier, combinedMessage, progressHtml, giftInCart) {
    if (!tier.products?.length) return '';
    
    const productsHtml = tier.products.map(p => {
      const isSelected = giftInCart && parseInt(giftInCart.variant_id) === parseInt(p.variantId);
      return `
        <div class="Carousel__Cell gwp-product-card ${tier.products.length == 1 ? 'only' : ''} ${isSelected ? 'selected' : ''}" data-variant="${p.variantId}">
          <img src="${p.image}" alt="${p.title}" class="gwp-product-image">
          <div class="gwp-product-title">${p.title}</div>
        </div>
      `;
    }).join('');
    
    const hasSelectedGiftInCart = giftInCart && tier.products.some(p => 
      parseInt(giftInCart.variant_id) === parseInt(p.variantId)
    );
    
    const buttonText = hasSelectedGiftInCart ? 'GIFT IN CART' : 'SELECT A GIFT';
    const buttonDisabled = hasSelectedGiftInCart ? 'disabled' : '';
    
    return `
      <div class="gwp-unlocked">
        <div class="gwp-product-slider Carousel">
          ${productsHtml}
        </div>
        <button class="gwp-add-button Button Button--primary" data-tier="${tier.tierIndex}" ${buttonDisabled}>${buttonText}</button>
      </div>
    `;
  }

  /**
   * Attach click listeners to gift products and add button
   * @param {Object} tier - Tier configuration
   * @param {Object} cart - Current cart state
   */
  attachListeners(tier, cart) {
    const products = document.querySelectorAll('.gwp-product-card');
    const button = document.querySelector('.gwp-add-button');
    if (!button) return;

    // Check if there's already a gift in cart for this tier
    const giftInCart = cart.items.find(item => 
      item.properties?._gwp_gift === 'true' && 
      parseInt(item.properties?._gwp_tier_index) === tier.tierIndex
    );

    // Pre-select the gift if it's already in cart
    if (giftInCart) {
      this.selectedGifts.set(tier.tierIndex, giftInCart.variant_id.toString());
    }

    products.forEach(product => {
      product.addEventListener('click', (e) => {
        // Prevent Flickity drag from triggering selection
        if (this.flickityInstance && this.flickityInstance.isDragging) return;
        
        const clickedVariantId = product.dataset.variant;
        const isAlreadyInCart = giftInCart && giftInCart.variant_id.toString() === clickedVariantId;
        
        // If clicking on the same gift that's already in cart, do nothing
        if (isAlreadyInCart) {
          return;
        }
        
        // Update selection
        products.forEach(p => p.classList.remove('selected'));
        product.classList.add('selected');
        this.selectedGifts.set(tier.tierIndex, clickedVariantId);
        
        // Enable button and change text to "CHANGE GIFT" (since user selected different gift)
        button.disabled = false;
        button.textContent = giftInCart ? 'CHANGE GIFT' : 'ADD TO CART';
      });
    });

    button.addEventListener('click', () => this.addGift(tier.tierIndex, button, giftInCart));
  }

  /**
   * Add selected gift to cart (or replace existing gift)
   * @param {Number} tierIndex - Tier index
   * @param {HTMLElement} button - Add button element
   * @param {Object} existingGift - Existing gift in cart (if any)
   */
  async addGift(tierIndex, button, existingGift) {
    const variantId = this.selectedGifts.get(tierIndex);
    if (!variantId) return;

    button.disabled = true;
    button.textContent = 'ADDING...';
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    try {
      // If there's an existing gift, remove it first
      if (existingGift) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingGift.key,
            quantity: 0
          })
        });
      }

      // Add the new gift
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
         },
        body: JSON.stringify({
          items: [{ 
            id: variantId, 
            quantity: 1, 
            properties: { 
              _gwp_gift: 'true', 
              _gwp_tier_index: tierIndex.toString() 
            } 
          }]
        })
      });

      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      
      // Trigger product:added event so theme.min.js updates the drawer
      document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
      
      button.textContent = existingGift ? 'CHANGED!' : 'ADDED!';
      button.disabled = true; // Keep disabled after adding
      
      // After delay, change to "GIFT IN CART"
      setTimeout(() => {
        button.textContent = 'GIFT IN CART';
        button.disabled = true;
      }, 1500);
    } catch (error) {
      console.error('GWP Error adding gift:', error);
      button.disabled = false;
      button.textContent = 'ERROR';
    }
  }

  /**
   * Remove gifts from cart that are no longer qualified
   * @param {Object} cart - Cart object
   * @param {Number} total - Cart total in cents
   * @param {Object} currentTier - Currently unlocked tier
   * @returns {Boolean} True if gifts were removed
   */
  async removeUnqualifiedGifts(cart, total, currentTier) {
    const giftsToRemove = [];
    
    // Find all gifts in cart
    cart.items.forEach(item => {
      if (item.properties?._gwp_gift === 'true') {
        const giftTierIndex = parseInt(item.properties._gwp_tier_index);
        const giftTier = this.config.tiers.find(t => t.tierIndex === giftTierIndex);
        
        // Check if this gift's tier is still unlocked
        if (!giftTier || total < giftTier.threshold) {
          giftsToRemove.push(item.key);
          console.log('GWP: Removing gift from tier', giftTierIndex, '- no longer qualified');
        }
      }
    });

    // Remove unqualified gifts
    if (giftsToRemove.length > 0) {
      const updates = {};
      giftsToRemove.forEach(key => {
        updates[key] = 0;
      });

      try {
        // Update the cart and trigger refresh
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        
        // Trigger cart refresh event
        document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
        
        return true; // Gift was removed
      } catch (error) {
        console.error('Error removing unqualified gifts:', error);
      }
    }
    
    return false; // No gifts removed
  }

  /**
   * Format cents to dollar string
   * @param {Number} cents - Amount in cents
   * @returns {String} Formatted money string
   */
  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }
  
  /**
  * Destroy GWP Manager
  * Clears the UI and removes event listeners
  */
  destroy() {
    console.log('GWPManager: destroy called');
    const container = document.getElementById('GWPContainer');
    if (container) {
      container.innerHTML = '';
      container.style.display = 'none';
    }
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
    }
  }
}


// Initialize GWP Manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { 
    window.gwpManager = new GWPManager();
    
    // Listen for cart updates to refresh GWP
    document.addEventListener('gwp:refresh', () => {
      if (window.gwpManager) {
        window.gwpManager.update();
      }
    });
  });
} else {
  window.gwpManager = new GWPManager();
  
  // Listen for cart updates to refresh GWP
  document.addEventListener('gwp:refresh', () => {
    if (window.gwpManager) {
      window.gwpManager.update();
    }
  });
}


